import { Eye, Hand, Heart, Hourglass, Images, MessageCircle, Play, Repeat2, TriangleAlert, Users } from 'lucide-react'
import { useState, type ReactNode } from 'react'
import { Link, useLocation } from 'react-router'
import type { FeedItem } from '../../api/types'
import { addedLabel, followerShare, formatAge, formatCount, formatPct, handleLabel } from '../../lib/format'
import type { OverlayState } from '../../lib/feedNav'
import { XBadge } from '../XBadge'
import { LanguageBadge } from '../post/ReelLanguage'
import { postHref, primaryViews, viewsLabel } from '../../lib/measurement'
import { MeasurementNote } from '../MeasurementNote'

interface TileProps {
  item: FeedItem
  isNew: boolean
  now: number
  /** Вид «Добавленные»: без ×N — доля от подписчиков, под ником — кто добавил. */
  added?: boolean
}

/** Плитка вкладки «Видео»: обложка 9:16 и цифры поверх. Тексты — в `TextCard`. */
export function FeedTile({ item, isNew, now, added = false }: TileProps) {
  const location = useLocation()
  const state: OverlayState = { backgroundLocation: location }

  return (
    <Link
      to={postHref(item)}
      state={state}
      className="group block min-w-0 rounded-xl outline-offset-2"
      aria-label={tileLabel(item)}
    >
      <div className="relative aspect-[9/14] overflow-hidden rounded-xl desk:aspect-[9/16] bg-surface ring-1 ring-line transition-[box-shadow] ring-inset group-hover:ring-fg/25">
        <VisualBody item={item} />
        <div className="absolute inset-x-2 top-2 flex flex-wrap items-center gap-1 pr-7">
          {added && item.xn === null ? <ShareBadge item={item} /> : <Badge item={item} />}
          <Marks item={item} added={added} />
          {item.kind === 'reel' && <LanguageBadge language={item.language} />}
        </div>
        {item.kind === 'reel' && (
          <span className="absolute top-2 right-2 grid size-7 place-items-center rounded-full bg-black/65 text-white">
            <Play size={12} fill="currentColor" aria-hidden />
          </span>
        )}
      </div>
      <div className="mt-1.5 flex min-w-0 items-center gap-1 px-0.5 text-xs text-muted">
        <span className="min-w-0 truncate font-medium text-fg">{handleLabel(item.platform, item.author.handle)}</span>
        <span className="shrink-0">· {formatAge(item.published_at, now)}</span>
        {item.siblings > 0 && (
          // Подпись видна и без наведения; в карточке это блок «Тот же пост у других».
          <span className="ml-auto shrink-0 whitespace-nowrap" title="Склеено с кросспостами или репостами того же поста">
            +{item.siblings} у других
          </span>
        )}
      </div>
      {isNew && <span className="ml-0.5 rounded bg-surface px-1.5 text-[11px] font-semibold text-link">новое</span>}
      {added && item.manual && <div className="mt-0.5 truncate px-0.5 text-xs text-muted">{addedLabel(item.manual)}</div>}
      {item.platform === 'instagram' && <div className="mt-0.5 px-0.5"><MeasurementNote value={item} compact /></div>}
    </Link>
  )
}

function tileLabel(item: FeedItem): string {
  const who = handleLabel(item.platform, item.author.handle)
  const x = item.xn !== null ? `, ×${item.xn.toFixed(1).replace('.', ',')}` : ''
  return `${who}${x}: ${item.first_phrase ?? item.caption_snippet ?? 'пост'}`
}

function Badge({ item }: { item: FeedItem }) {
  if (item.metric_view === 'meta' && item.xn_status === 'warmup') return <span className="rounded-md bg-black/60 px-1.5 py-0.5 text-[11px] text-white">Набираем базу</span>
  if (item.metric_view === 'meta' && item.xn_status === 'stale') return <span className="rounded-md bg-black/60 px-1.5 py-0.5 text-[11px] text-white">{item.xn === null ? 'Нет свежей оценки' : `×${item.xn.toFixed(1).replace('.', ',')} · устарело`}</span>
  if (item.xn !== null) return <XBadge value={item.xn} className="shadow-sm" />
  if (item.repost) {
    return (
      <span
        className="inline-flex h-5 items-center gap-1 rounded-md bg-black/60 px-1.5 text-xs leading-none font-medium text-white"
        title="Репост: ×N не считается, оригинал не из наших источников"
      >
        <Repeat2 size={12} />
        репост
      </span>
    )
  }
  return null
}

/** Автор не отслеживается — ×N нет, вместо него просмотры к подписчикам. Подписчиков не знаем — бейджа нет. */
function ShareBadge({ item }: { item: FeedItem }) {
  const share = followerShare(primaryViews(item), item.author.followers)
  if (share === null) return null
  return (
    <span
      className="inline-flex h-5 items-center gap-1 rounded-md bg-black/60 px-1.5 text-xs leading-none font-semibold text-white tabular-nums"
      title={`Просмотры — ${formatPct(share)} от подписчиков автора. ×N нет: автор не отслеживается`}
    >
      <Users size={12} aria-hidden />
      {formatPct(share)}
    </span>
  )
}

function Marks({ item, added }: { item: FeedItem; added: boolean }) {
  const cls = 'inline-flex size-5 items-center justify-center rounded-md bg-black/60 text-white'
  // Ранняя оценка и «мало данных» — про ×N: без него в «Добавленных» не нужны.
  if (added && item.xn === null) return null
  return (
    <>
      {!added && item.manual && (
        <span className={cls} title="Добавлен вручную: лежит и в «Добавленных»">
          <Hand size={12} aria-label="добавлен вручную" />
        </span>
      )}
      {item.is_early && (
        <span className={cls} title="Ранняя оценка — пост ещё набирает">
          <Hourglass size={12} aria-label="ранняя оценка" />
        </span>
      )}
      {item.low_data && (
        <span className={cls} title="Мало данных у автора — цифре можно верить меньше">
          <TriangleAlert size={12} aria-label="мало данных" />
        </span>
      )}
      {item.repost && item.xn !== null && (
        <span className={cls} title={`Репост из ${item.repost.origin_handle ? `@${item.repost.origin_handle}` : 'канала'}`}>
          <Repeat2 size={12} aria-label="репост" />
        </span>
      )}
    </>
  )
}

function VisualBody({ item }: { item: FeedItem }) {
  const [broken, setBroken] = useState(false)
  const m = item.metrics
  return (
    <>
      {item.thumb_url && !broken ? (
        <img
          src={item.thumb_url}
          alt=""
          loading="lazy"
          decoding="async"
          onError={() => setBroken(true)}
          className="absolute inset-0 size-full object-cover transition-transform duration-300 motion-safe:group-hover:scale-[1.02]"
        />
      ) : (
        // Ссылка Instagram устарела, а в кэш обложка не попала: вместо неё начало ролика.
        <div className="absolute inset-0 flex items-center p-3 pt-10 pb-12">
          <p className="line-clamp-[9] text-[13px] leading-snug text-pretty text-muted">
            {item.first_phrase ?? item.caption_snippet ?? 'Обложка недоступна'}
          </p>
        </div>
      )}
      {item.kind !== 'reel' && (
        <span className="absolute right-2 bottom-9 inline-flex size-5 items-center justify-center rounded-md bg-black/60 text-white" title={item.kind === 'carousel' ? 'Карусель' : 'Картинка'}>
          <Images size={12} />
        </span>
      )}
      <div className="absolute inset-x-0 bottom-0 grid gap-1 bg-linear-to-t from-black/75 via-black/40 to-transparent px-2 pt-6 pb-1.5 text-[11px] font-medium text-white desk:gap-x-2.5 desk:text-[12px]">
        {item.thumb_url && !broken && (item.first_phrase || item.caption_snippet) && (
          <p className="line-clamp-2 text-xs leading-tight font-semibold text-pretty desk:text-sm">
            {item.first_phrase ?? item.caption_snippet}
          </p>
        )}
        <div className="flex flex-wrap gap-x-2 gap-y-0.5">
          {primaryViews(item) !== null && <Stat icon={<Eye size={13} />} value={primaryViews(item)!} label={viewsLabel(item)} />}
          {m.likes !== null && <Stat icon={<Heart size={13} />} value={m.likes} label="лайки" />}
          {m.comments !== null && <Stat icon={<MessageCircle size={13} />} value={m.comments} label="комментарии" />}
        </div>
      </div>
    </>
  )
}

function Stat({ icon, value, label }: { icon: ReactNode; value: number; label: string }) {
  return (
    <span className="inline-flex items-center gap-1 tabular-nums" title={label}>
      {icon}
      {formatCount(value)}
    </span>
  )
}

export function TileSkeleton() {
  return (
    <div aria-hidden>
      <div className="aspect-[9/14] rounded-xl desk:aspect-[9/16] bg-surface ring-1 ring-line ring-inset motion-safe:animate-pulse" />
      <div className="mt-2 h-2.5 w-2/3 rounded bg-surface motion-safe:animate-pulse" />
    </div>
  )
}
